import { useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { useEscapeKey } from "@/hooks/useEscapeKey";

/**
 * Archive / Restore / Founder-only Purge controls for a Client, Project or Contact detail
 * page. Archive is a soft field write (Notion `Archived` checkbox, read-back verified by the
 * backend); Purge is a Founder-only hard-delete via Notion's trash API, gated by a typed
 * confirmation and (for Clients) blocked outright if active children exist.
 */
const ENTITY_LABEL = { projects: "project", clients: "client", contacts: "contact" };

export function ArchiveControls({ entity, record, canWrite, isFounder, onChanged, onPurged, variant = "buttons" }) {
  const entityLabel = ENTITY_LABEL[entity] || entity;
  const [confirm, setConfirm] = useState(null); // { children } | null
  const [purging, setPurging] = useState(false);
  const [purgeName, setPurgeName] = useState("");
  const [busy, setBusy] = useState(false);

  const openArchiveConfirm = async () => {
    if (entity !== "clients") { setConfirm({ children: null }); return; }
    try {
      const r = await api.get(`/v1/clients/${record.id}/archive-check`);
      setConfirm({ children: r.data });
    } catch {
      setConfirm({ children: null });
    }
  };

  const doArchive = async () => {
    setBusy(true);
    try {
      await api.post(`/v1/${entity}/${record.id}/archive`);
      toast.success("Archived", { description: "Hidden from default lists. Restore any time." });
      setConfirm(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Couldn't archive");
    } finally { setBusy(false); }
  };

  const doRestore = async () => {
    setBusy(true);
    try {
      await api.post(`/v1/${entity}/${record.id}/restore`);
      toast.success("Restored");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Couldn't restore");
    } finally { setBusy(false); }
  };

  const doPurge = async () => {
    setBusy(true);
    try {
      await api.post(`/v1/${entity}/${record.id}/purge`, { confirm_name: purgeName });
      toast.success("Purged permanently", { description: "Moved to Notion trash and removed from the platform." });
      setPurging(false);
      onPurged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "active_children_exist") {
        toast.error("Blocked: active children exist", {
          description: `${detail.active_projects || 0} project(s), ${detail.active_contacts || 0} contact(s) still reference this. Archive or reassign them first.`,
        });
      } else if (detail?.code === "confirm_name_mismatch") {
        toast.error("Name doesn't match. Purge cancelled");
      } else {
        toast.error(detail?.error || "Couldn't purge");
      }
    } finally { setBusy(false); }
  };

  const isMenu = variant === "menu";

  return (
    <div className={isMenu ? "flex flex-col" : "inline-flex items-center gap-2"} data-testid={`archive-controls-${entity}-${record.id}`}>
      {canWrite && !record.archived && (
        <button onClick={openArchiveConfirm} data-testid={`archive-btn-${entity}`}
          className={isMenu
            ? "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] text-[#495053] hover:bg-[#FAFAF9]"
            : "inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-orange-400/50 hover:text-orange-600"}>
          <Archive className="h-3.5 w-3.5" /> Archive{isMenu ? ` ${entityLabel}` : ""}
        </button>
      )}
      {canWrite && record.archived && (
        <button onClick={doRestore} disabled={busy} data-testid={`restore-btn-${entity}`}
          className={isMenu
            ? "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] text-emerald-600 hover:bg-[#FAFAF9] disabled:opacity-50"
            : "inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-xs text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-50"}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArchiveRestore className="h-3.5 w-3.5" />} Restore
        </button>
      )}
      {isMenu && canWrite && isFounder && <div className="h-px bg-[#E5E6EC] my-1.5 mx-1" />}
      {isFounder && (
        <button onClick={() => { setPurgeName(""); setPurging(true); }} data-testid={`purge-btn-${entity}`}
          className={isMenu
            ? "flex flex-col items-start gap-0.5 rounded-md px-2.5 py-2 text-left text-[#B42318] hover:bg-[#FEF3F2]"
            : "inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/5 px-3 py-1.5 text-xs text-red-600 hover:bg-red-500/10"}>
          {isMenu ? (
            <>
              <span className="text-[13px] font-medium flex items-center gap-2.5"><Trash2 className="h-3.5 w-3.5" /> Purge permanently…</span>
              <span className="text-[10.5px] text-[#9BA1A4] pl-6">Requires typing the project name</span>
            </>
          ) : (<><Trash2 className="h-3.5 w-3.5" /> Purge</>)}
        </button>
      )}

      {confirm && (
        <ConfirmModal
          title={`Archive "${record.name}"?`}
          testId={`archive-confirm-${entity}`}
          onCancel={() => setConfirm(null)}
          onConfirm={doArchive}
          busy={busy}
          confirmLabel="Archive"
        >
          <p className="text-sm text-[#57534E]">
            Hides this record from default lists. Fully reversible via Restore at any time.
          </p>
          {confirm.children?.count > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-orange-500/30 bg-orange-500/5 p-3 text-xs text-orange-600" data-testid="archive-active-children-warning">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                This client has {confirm.children.active_projects.length} active project(s) and {confirm.children.active_contacts.length} active contact(s).
                Archiving the client won't affect them, but they'll stay visible independently.
              </span>
            </div>
          )}
        </ConfirmModal>
      )}

      {purging && (
        <ConfirmModal
          title={`Permanently purge "${record.name}"?`}
          testId={`purge-confirm-${entity}`}
          onCancel={() => setPurging(false)}
          onConfirm={doPurge}
          busy={busy}
          confirmLabel="Purge Permanently"
          danger
          confirmDisabled={purgeName.trim() !== record.name.trim()}
        >
          <p className="text-sm text-[#57534E]">
            This moves the record to Notion's trash and removes it from the platform entirely. This cannot be undone from here.
          </p>
          <label className="mt-3 block text-[10px] uppercase tracking-wider text-[#A8A29E]">
            Type <span className="font-mono text-[#1C1C1E] normal-case">{record.name}</span> to confirm
          </label>
          <input
            data-testid={`purge-confirm-input-${entity}`}
            value={purgeName}
            onChange={(e) => setPurgeName(e.target.value)}
            className="mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] focus:outline-none focus:border-red-400/50"
          />
        </ConfirmModal>
      )}
    </div>
  );
}

const ConfirmModal = ({ title, children, onCancel, onConfirm, busy, confirmLabel, confirmDisabled, danger, testId }) => {
  useEscapeKey(onCancel);
  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid={testId}>
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative w-full max-w-sm rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5 shadow-2xl">
      <h3 className="font-editorial text-lg tracking-tight text-[#1C1C1E]">{title}</h3>
      <div className="mt-2">{children}</div>
      <div className="mt-5 flex gap-2">
        <button onClick={onCancel} data-testid={`${testId}-cancel`} className="flex-1 rounded-md border border-[#E7E5E4] px-4 py-2 text-xs text-[#57534E] hover:text-[#1C1C1E]">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
          data-testid={`${testId}-submit`}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2 text-xs font-medium disabled:opacity-40 ${danger ? "bg-red-500 text-white hover:bg-red-400" : "bg-[#FFC817] text-[#1C1C1E] hover:bg-[#E6B415]"}`}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {confirmLabel}
        </button>
      </div>
    </div>
  </div>
  );
};
